import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, ConfigProvider, Form, Input, message, Select, Space, Spin, Switch } from 'antd'
import { OpenStreetMapProvider } from 'leaflet-geosearch'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { addLocation, bulkAddLocationTags, fetchLocationTags, getGeoByAddress, modLocation } from '../../apis/location'
import Map from './components/Map'
import { BackButton, FormButton, LocationFormWrapper, LocationWrapper, WhiteWrapper } from './location.styled'
const provider = new OpenStreetMapProvider()

function LocationForm (props) {
  const { goBackPath, addMode, editMode, viewOnly } = props
  const { t: ln_t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = location || {}
  const { data } = state || {}
  const user = useSelector((state) => state.user)
  const { nowBrand, nowGroup } = user || {}
  const [tagList, setTagList] = useState([])
  const [selectedLocation, setSelectedLocation] = useState({})
  const [locationInfo, setLocationInfo] = useState({})
  const goBack = () => navigate(goBackPath)

  // ----- to search location by place or address ----- //
  const [searchType, setSearchType] = useState('place')
  const [searching, setSearching] = useState(false)
  const [searchOptions, setSearchOptions] = useState([])
  const [inputPressing, setInputPressing] = useState(false)
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const inputRef = useRef()

  const searchTypeOptions = [{ value: 'place', label: ln_t('location.place') }, { value: 'address', label: ln_t('location.address') }]
  const handleSearchClicked = () => {
    setInputPressing(true)
    setSearchOptions([])
  }

  const handleSearch = async value => {
    if (inputRef?.current) inputRef.current.focus()
    if (searchType === 'place') {
      setSearching(true)
      const results = await provider.search({ query: value })
      setSearchOptions(results || [])
      setInputPressing(false)
      setSearching(false)
      return
    }
    const result = await getGeoByAddress({ address: value })
    if (result?.status === 200) {
      const { px: longitude, py: latitude } = result.data || {}
      if (!longitude) return message.error(ln_t('message.error.noSearchResult'))
      const n = { ...locationInfo, longitude, latitude, searchKeyword: value }
      delete n.bounds
      setLocationInfo(n)
    }
  }

  // ----- form functions ----- //
  const defPlaceholder = (name, type) => {
    const message = type === 'input' ? ln_t('util.pleaseEnter') : type === 'select' ? ln_t('util.pelaseSelect') : ''
    return `${message} ${ln_t(`location.${name}`)}`
  }
  const defConfig = (name, type, required = true) => ({
    name, label: ln_t(`location.${name}`), rules: [{ required, message: defPlaceholder(name, type) }]
  })

  const handleAdd = async reqBody => {
    const res = await addLocation(reqBody)
    if (res?.status === 201) {
      message.success(ln_t('message.success.create'))
      goBack()
    }
  }

  const handleMod = async (id, reqBody) => {
    const res = await modLocation(id, reqBody)
    if (res?.status === 200) {
      message.success(ln_t('message.success.modify'))
      goBack()
    }
  }
  const handleSave = async ({ name, status, tags }) => {
    const { longitude, latitude, radius } = selectedLocation || {}
    if (!longitude || !latitude || !radius) return message.error(ln_t('message.error.noLocationSelected'))
    setLoadingSubmit(true)
    const reqBody = {
      ...selectedLocation,
      name,
      tags: tags?.join('@'),
      status: status ? 1 : 0,
      locationType: 'geo',
      brandId: nowBrand,
      groupId: nowGroup,
      geoTranslate: 2
    }
    const newTags = tags?.filter(i => !tagList.includes(i)) || []
    if (newTags.length) {
      const addTag = await bulkAddLocationTags({ locationTags: newTags.map(i => ({ name: i, brandId: nowBrand })) })
      if (addTag?.status !== 200) return
    }
    addMode ? await handleAdd(reqBody) : await handleMod(data?.id, reqBody)
    setLoadingSubmit(false)
  }

  const getLocationTag = async () => {
    const res = await fetchLocationTags({ maxResults: 999, brandId: nowBrand, status: 1 })
    if (res?.status) setTagList(res?.data?.map(i => i.name))
  }
  useEffect(() => {
    if ((!addMode && !editMode) || (editMode && !data)) return goBack()
    getLocationTag()
    if (addMode) return
    const { name, tags = '' } = data
    form.setFieldsValue({ name, tags: tags?.split('@') })
    setLocationInfo(data)
  }, [])

  return (
    <>
      <BackButton>
        <ArrowLeftOutlined onClick={() => goBack()} />
        {viewOnly ? `${ln_t('sidebar.location')} ${ln_t('util.detail')}` : `${ln_t(`util.${addMode ? 'addNew' : 'edit'}`)} ${ln_t('sidebar.location')}`}
      </BackButton>
      <ConfigProvider theme={{ token: { colorPrimary: 'rgb(71, 182, 175)', colorPrimaryHover: 'rgba(71, 182, 175,.8)' } }}>
        <WhiteWrapper>
          <Form form={form} requiredMark={false} onFinish={e => handleSave(e)} disabled={viewOnly}>
            <LocationFormWrapper>
              <Form.Item {...defConfig('status', 'input')} valuePropName="checked" initialValue={addMode ? true : data?.status === 1}>
                <Switch
                  checkedChildren={ln_t('util.enable')}
                  unCheckedChildren={ln_t('util.disable')}
                />
              </Form.Item>
              <Form.Item {...defConfig('name', 'input')} style={{ flexGrow: 1 }}>
                <Input placeholder={defPlaceholder('name', 'input')} />
              </Form.Item>
              <Form.Item {...defConfig('tags', 'select', false)} style={{ flexGrow: 1 }}>
                <Select mode="tags" placeholder={defPlaceholder('tags', 'select')} options={tagList?.map(i => ({ value: i, label: i }))} />
              </Form.Item>
            </LocationFormWrapper>
            {editMode
              ? ''
              : (<Space.Compact style={{ width: '100%', marginTop: '16px' }}>
                <Select value={searchType} onChange={e => setSearchType(e)} options={searchTypeOptions} style={{ width: '100px' }} />
                {searchType === 'place'
                  ? <>
                    <Select
                      placeholder={defPlaceholder('place', 'input')}
                      style={{ width: 'calc(100% - 132px)' }}
                      showSearch
                      showArrow={false}
                      filterOption={false}
                      ref={inputRef}
                      onFocus={() => setInputPressing(true)}
                      onSelect={e => setLocationInfo(JSON.parse(e))}
                      options={searchOptions?.map(({ label, x: longitude, y: latitude, bounds }) => ({ label, value: JSON.stringify({ latitude, longitude, bounds, searchKeyword: label }) }))}
                      onInputKeyDown={({ code, target }) => code === 'Enter' ? handleSearch(target.value) : handleSearchClicked() }
                      dropdownRender={(originNode) => searching
                        ? <Spin size="small" className='ml-3' />
                        : inputPressing
                          ? <span className='ml-3' style={{ color: 'rgba(0,0,0,.25)' }}>{ln_t('location.searchInfo')}</span>
                          : originNode}
                    />
                    <Button type="primary" icon={<SearchOutlined />} />
                  </>
                  : <Input.Search
                      placeholder={defPlaceholder('address', 'input')}
                      enterButton={<Button type="primary" size="small" icon={<SearchOutlined />} />}
                      onSearch={e => handleSearch(e)}
                      onKeyPress={e => { if (e.key === 'Enter') e.preventDefault() }} // to prevent submitting form on enter
                    />
                }
              </Space.Compact>)
            }
            <LocationWrapper fullScreen>
              <Map
                editable={!viewOnly}
                editMode={editMode && !viewOnly}
                locationInfo={locationInfo}
                setLocationInfo={setLocationInfo}
                selectedLocation={selectedLocation}
                setSelectedLocation={setSelectedLocation}
                urlProps={data || {}}
              />
            </LocationWrapper>
            {viewOnly
              ? ''
              : <FormButton>
                  <Button type="default" onClick={goBack}>{ln_t('util.cancel')}</Button>
                  <Button type="primary" htmlType="submit" loading={loadingSubmit}>{ln_t('util.confirm')}</Button>
                </FormButton>
            }
          </Form>
        </WhiteWrapper>

      </ConfigProvider>
    </>
  )
}

export default LocationForm
